import React, { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { CATEGORIES, CATEGORY_MAP, buildProductName } from "@/lib/catalog";

/**
 * Cascading product picker: Category → Variant → Color → Size (where applicable).
 * `value` is an object { category, variant, color, size, custom_size, product_name, unit }.
 * `onChange(next)` is called with the updated object every step.
 */
export default function ProductPicker({ value, onChange, testidPrefix = "pick" }) {
  const cat = CATEGORY_MAP[value.category];

  // Auto-refresh product_name & unit whenever selections change
  useEffect(() => {
    const nextName = buildProductName(value);
    const nextUnit = cat?.defaultUnit || value.unit || "pcs";
    if (nextName !== value.product_name || nextUnit !== value.unit) {
      onChange({ ...value, product_name: nextName, unit: nextUnit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.category, value.variant, value.color, value.size, value.custom_size]);

  const setCategory = (k) => onChange({ ...value, category: k, variant: "", color: "", size: "", custom_size: "" });
  const setVariant = (k) => onChange({ ...value, variant: k });
  const setColor = (k) => onChange({ ...value, color: k });
  const setSize = (k) => onChange({ ...value, size: k });
  const setCustomSize = (v) => onChange({ ...value, custom_size: v });

  const colorGroups = useMemo(() => {
    if (!cat) return [];
    const groups = {};
    cat.colors.forEach((c) => {
      const g = c.collection || "default";
      if (!groups[g]) groups[g] = [];
      groups[g].push(c);
    });
    return Object.entries(groups);
  }, [cat]);

  const variantLabel = cat?.variants.find((v) => v.key === value.variant)?.label || "Choose variant";
  const colorLabel = cat?.colors.find((c) => c.key === value.color)?.label || "Choose color";
  const sizeLabel = cat?.sizes.find((s) => s.key === value.size)?.label || "Choose size";

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* Category */}
      <div className="col-span-2">
        <Label className="text-xs">Category</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid={`${testidPrefix}-category`} className="w-full justify-between mt-2">
              {cat?.label || "Choose category"}<ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-72">
            {CATEGORIES.map((c) => (
              <DropdownMenuItem key={c.key} onClick={() => setCategory(c.key)}>{c.label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Variant */}
      {cat && cat.variants.length > 0 && (
        <div>
          <Label className="text-xs">Variant / Shape</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid={`${testidPrefix}-variant`} className="w-full justify-between mt-2">
                {variantLabel}<ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 max-h-72 overflow-y-auto">
              {cat.variants.map((v) => (
                <DropdownMenuItem key={v.key} onClick={() => setVariant(v.key)}>{v.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Color */}
      {cat && cat.colors.length > 0 && (
        <div>
          <Label className="text-xs">Color</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid={`${testidPrefix}-color`} className="w-full justify-between mt-2">
                {colorLabel}<ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64 max-h-80 overflow-y-auto">
              {colorGroups.map(([grp, items], gi) => (
                <React.Fragment key={grp}>
                  {colorGroups.length > 1 && (
                    <>
                      {gi > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuLabel className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">
                        {grp === "signature" ? "Signature · 15" : "Standard"}
                      </DropdownMenuLabel>
                    </>
                  )}
                  {items.map((c) => (
                    <DropdownMenuItem key={c.key} onClick={() => setColor(c.key)}>{c.label}</DropdownMenuItem>
                  ))}
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Size */}
      {cat && cat.sizes.length > 0 && (
        <div className="col-span-2">
          <Label className="text-xs">Size</Label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {cat.sizes.map((s) => (
              <Button
                key={s.key} type="button" variant={value.size === s.key ? "default" : "outline"}
                onClick={() => setSize(s.key)} data-testid={`${testidPrefix}-size-${s.key}`}
                className={value.size === s.key ? "bg-[#1C1C1A] hover:bg-black text-white h-9" : "h-9"}
              >
                {s.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Custom size (countertops) */}
      {cat?.customSize && (
        <div className="col-span-2">
          <Label className="text-xs">Custom size (LxW)</Label>
          <Input value={value.custom_size || ""} onChange={(e) => setCustomSize(e.target.value)} placeholder="e.g. 96×26 inches" className="mt-2" data-testid={`${testidPrefix}-customsize`} />
        </div>
      )}

      {/* Auto-generated product name preview */}
      {value.product_name && (
        <div className="col-span-2 bg-[#F1F1EF] border border-[#D8D8D5] rounded-md p-2 text-xs text-[#1C1C1A]">
          <span className="text-[#6E6E6A] tracking-[0.15em] uppercase text-[9px] mr-2">Product</span>
          <span className="font-medium">{value.product_name}</span>
        </div>
      )}
    </div>
  );
}
